<template>
  <div v-if="isReady" style="padding: 20px;">
    <div class="text-size-20">
      商品の需要予測に基づいて、FBAへの納品をご提案します。Amazonが算出した値を使用しています。
    </div>
    <div class="panel-body">
      <premium-modal v-if="cantViewContent" />
      <div class="filter-container">
        <div class="flexbox gap-20">
          <div class="filter-keyword">
            <span class="search-icon-left">🔎</span>
            <input
              v-model="keyword"
              type="text"
              class="search-input"
              placeholder="ASIN、SKU、FNSKU、商品名"
              autocomplete="off"
              @keyup.enter="handleFilter"
            >
            <button class="clear-search" @click="keyword = ''">✕</button>
          </div>
          <div class="filter-actions">
            <button class="btn btn-primary mr-r-10" type="button" @click="handleFilter">
              <i class="fa fa-search" aria-hidden="true"></i> 検索
            </button>
            <button class="btn" type="button" @click="resetFilters">
              <i class="fa fa-undo" aria-hidden="true"></i> すべてクリア
            </button>
          </div>
        </div>
      </div>
      <div class="table-toolbar">
        <div class="toolbar-left">
          <span class="snapshot-date">{{ snapshotDate }} <span class="text-size-14">時点の情報</span></span>
          <span class="snapshot-note">在庫数、納品数の目安は最新のデータとは異なる可能性があるのでご注意ください。</span>
        </div>
        <div class="toolbar-right">
          <single-select
            v-model="sortKey"
            :items="sortOptions"
            placeholder="並び替え:"
          />
          <pagination-next-page
            :totalPage="totalPage"
            :currentPage="currentPage"
            :paginationStorageKey="paginationStorageKey"
            @updateCurrentPage="currentPage = $event"
          />
        </div>
      </div>
      <div class="table-wrapper">
        <table class="table table-bordered fba-table">
          <thead>
            <tr class="header-row">
              <th class="col-product"></th>
              <th class="col-stockcrew sortable-th" @click="toggleHeaderSort('stockcrewAvailableStock')">
                <div class="th-group">STOCKCREW</div>
                <div class="th-sub">
                  販売可能在庫数
                  <i v-if="headerSort.field === 'stockcrewAvailableStock'" :class="headerSort.dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                </div>
              </th>
              <th class="col-amazon sortable-th" @click="toggleHeaderSort('amazonAvailableStock')">
                <div class="th-group">Amazon</div>
                <div class="th-sub">
                  販売可能在庫数
                  <i v-if="headerSort.field === 'amazonAvailableStock'" :class="headerSort.dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                </div>
              </th>
              <th class="col-amazon sortable-th" @click="toggleHeaderSort('amazonStockoutDate')">
                <div class="th-group">Amazon</div>
                <div class="th-sub">
                  在庫がなくなる推定日
                  <i v-if="headerSort.field === 'amazonStockoutDate'" :class="headerSort.dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                </div>
              </th>
              <th class="col-amazon sortable-th" @click="toggleHeaderSort('amazonAdditionalQty')">
                <div class="th-group">Amazon</div>
                <div class="th-sub">
                  追加納品数の目安
                  <i v-if="headerSort.field === 'amazonAdditionalQty'" :class="headerSort.dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                </div>
              </th>
              <th class="col-amazon sortable-th" @click="toggleHeaderSort('amazonShipmentDate')">
                <div class="th-group">Amazon</div>
                <div class="th-sub">
                  納品(算出)の目安日
                  <i v-if="headerSort.field === 'amazonShipmentDate'" :class="headerSort.dir === 'asc' ? 'fa fa-sort-asc' : 'fa fa-sort-desc'" aria-hidden="true"></i>
                </div>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr v-if="products.length === 0">
              <td colspan="6" class="text-center no-data">データが見つかりません。</td>
            </tr>
            <tr v-for="(item, index) in products" :key="index">
              <td class="col-product">
                <div class="flexbox gap-10 no-wrap">
                  <img v-if="item.image" :src="item.image" class="product-img" alt="">
                  <div v-else class="product-img-placeholder"></div>
                  <div class="product-info">
                    <a
                      :href="'https://www.amazon.co.jp/gp/offer-listing/' + item.asin"
                      target="_blank"
                      class="product-name text--underline"
                    >{{ item.productName }}</a>
                    <div class="flexbox product-meta">
                      <span class="meta-pair"><span class="meta-label">SKU：</span><span class="meta-tag">{{ item.sku }}</span></span>
                      <span class="meta-pair"><span class="meta-label">ASIN：</span><span class="meta-tag">{{ item.asin }}</span></span>
                      <span class="meta-pair"><span class="meta-label">FNSKU：</span><span class="meta-tag">{{ item.fnsku }}</span></span>
                    </div>
                  </div>
                </div>
              </td>
              <td
                class="col-stockcrew cell-with-icon"
              >
                <img :src="iconUrls['salmonSc']" class="img-responsive sc-image cell-icon" alt="STOCKCREW">
                <span class="qty">{{ formatNumber(item.stockcrewAvailableStock) }} 個</span>
              </td>
              <td
                class="col-amazon-data cell-with-icon"
                v-tooltip="{ content: inboundTooltip(item.amazonInboundQty), html: true }"
              >
                <img :src="iconUrls['Amazon']" class="img-responsive amazon-image cell-icon" alt="Amazon">
                <span class="qty">{{ formatNumber(item.amazonAvailableStock) }} 個</span>
              </td>
              <td class="col-amazon-data text-center">
                <span
                  v-if="item.amazonStockoutDate"
                  :class="{ 'text-danger': isNearDate(item.amazonStockoutDate) }"
                >
                  {{ item.amazonStockoutDate }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td
                class="col-amazon-data text-center"
                :class="getAdditionalQtyClass(item.amazonAdditionalQty)"
                v-tooltip="{ content: salesHistoryTooltip(item), html: true }"
              >
                <span v-if="item.amazonAdditionalQty != null">
                  {{ item.amazonAdditionalQty > 0 ? '+' : '' }}
                  {{ formatNumber(item.amazonAdditionalQty) }} 個
                </span>
                <span v-else class="text-muted">-</span>
              </td>
              <td
                class="col-amazon-data text-center"
                :class="getAdditionalQtyClass(item.amazonAdditionalQty)"
              >
                <span
                  v-if="item.amazonShipmentDate"
                  :class="{ 'text-danger': isNearDate(item.amazonShipmentDate) }"
                >
                  {{ item.amazonShipmentDate }}
                </span>
                <span v-else class="text-muted">-</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script>
import SalemonAgentService from '@/services/salemonagentservice'
import SingleSelect from '@/components/SingleSelect'
import PaginationNextPage from '@/components/PaginationNextPage'
import Util from '@/common/utility'
import PremiumModal from './components/PremiumModalOverlay'
import EventService from '@/services/eventservice'
import eventCode from '@/common/event-code'

export default {
  components: {
    SingleSelect,
    PaginationNextPage,
    PremiumModal
  },
  data () {
    return {
      isReady: false,
      loader: null,
      keyword: '',
      snapshotDate: '',
      sortKey: 'asin_asc',
      sortOptions: [
        { id: 'asin_asc', text: 'ASIN: A → Z' },
        { id: 'asin_desc', text: 'ASIN: Z → A' },
        { id: 'sku_asc', text: 'SKU: A → Z' },
        { id: 'sku_desc', text: 'SKU: Z → A' },
        { id: 'fnsku_asc', text: 'FNSKU: A → Z' },
        { id: 'fnsku_desc', text: 'FNSKU: Z → A' },
        { id: 'productName_asc', text: '商品名: A → Z' },
        { id: 'productName_desc', text: '商品名: Z → A' }
      ],
      headerSort: { field: null, dir: 'asc' },
      currentPage: 1,
      totalPage: 1,
      paginationStorageKey: 'fbaProductSales',
      allProducts: [],
      products: []
    }
  },
  computed: {
    cantViewContent () {
      return this.$store.getters.getUser.memberPlan === '0' || this.$store.getters.getUser.isPremiumTrial
    },
    iconUrls () {
      return this.$store.getters.getWareHouseIcons
    },
    perPage () {
      return this.$store.getters.getPaginationRow(this.paginationStorageKey)
    }
  },
  watch: {
    sortKey () { this.handleFilter() },
    perPage () { this.handleFilter() },
    currentPage (newVal, oldVal) {
      if (newVal !== oldVal) this.applyPage()
    }
  },
  created () {
    this.init()
  },
  methods: {
    async init () {
      this.loader = this.$loading.show()
      await this.getFbaProductSales()
      this.isReady = true
      this.loader && this.loader.hide()
    },
    async getFbaProductSales () {
      await SalemonAgentService.getFbaProductSales()
        .then((response) => {
          this.allProducts = response.data.products
          this.snapshotDate = response.data.snapshotDate
          this.applyFilteredData(this.getFilteredSorted(), true)
        })
        .catch((error) => {
          this.$events.fire('handleError', error)
        })
    },
    getFilteredSorted () {
      let filtered = this.allProducts.slice()
      if (this.keyword.trim()) {
        const kw = this.keyword.trim().toLowerCase()
        filtered = filtered.filter(p =>
          (p.asin && p.asin.toLowerCase().includes(kw)) ||
          (p.sku && p.sku.toLowerCase().includes(kw)) ||
          (p.fnsku && p.fnsku.toLowerCase().includes(kw)) ||
          (p.productName && p.productName.toLowerCase().includes(kw))
        )
      }
      const [dropField, dropDir] = this.sortKey.split('_')
      filtered.sort((a, b) => {
        if (this.headerSort.field) {
          const { field, dir } = this.headerSort
          const va = a[field]
          const vb = b[field]
          let cmp = 0
          if (va == null && vb == null) { cmp = 0 } else if (va == null) { cmp = 1 } else if (vb == null) { cmp = -1 } else {
            cmp = typeof va === 'number' && typeof vb === 'number'
              ? va - vb
              : va.toString().toLowerCase().localeCompare(vb.toString().toLowerCase())
          }
          if (cmp !== 0) return dir === 'desc' ? -cmp : cmp
        }
        const va = (a[dropField] || '').toString().toLowerCase()
        const vb = (b[dropField] || '').toString().toLowerCase()
        const cmp = va.localeCompare(vb)
        return dropDir === 'desc' ? -cmp : cmp
      })
      return filtered
    },
    applyFilteredData (filtered, resetPage) {
      if (resetPage) this.currentPage = 1
      this.totalPage = Math.max(1, Math.ceil(filtered.length / this.perPage))
      const start = (this.currentPage - 1) * this.perPage
      this.products = filtered.slice(start, start + this.perPage)
    },
    async withLoader (fn) {
      const loader = this.$loading.show()
      try {
        await new Promise(resolve => setTimeout(resolve, 300))
        fn()
      } finally {
        loader.hide()
      }
    },
    handleFilter () {
      let logParams = {
        name: eventCode.salemon_agent.fba_product_sales.click_search_btn,
        properties: {
          keyword: this.keyword
        }
      }
      EventService.postTrackLog(logParams)
      this.withLoader(() => this.applyFilteredData(this.getFilteredSorted(), true))
    },
    applyPage () {
      this.withLoader(() => this.applyFilteredData(this.getFilteredSorted(), false))
    },
    toggleHeaderSort (field) {
      if (this.headerSort.field === field) {
        this.headerSort.dir = this.headerSort.dir === 'asc' ? 'desc' : 'asc'
      } else {
        this.headerSort = { field, dir: 'asc' }
      }
      this.handleFilter()
    },
    resetFilters () {
      this.keyword = ''
      this.headerSort = { field: null, dir: 'asc' }
      this.handleFilter()
    },
    formatNumber (val) {
      return Util.formatMoney(val)
    },
    inboundTooltip (inboundQty) {
      const total = inboundQty.preparing + inboundQty.shipped + inboundQty.receiving
      return `<div class="fba-tooltip"><div class="fba-tooltip-title">納品中の数量（${total}個）</div><div class="fba-tooltip-row"><span>・準備中：</span><span>${inboundQty.preparing}個</span></div><div class="fba-tooltip-row"><span>・発送済：</span><span>${inboundQty.shipped}個</span></div><div class="fba-tooltip-row"><span>・受領中：</span><span>${inboundQty.receiving}個</span></div></div>`
    },
    salesHistoryTooltip (item) {
      return `<div class="fba-tooltip"><div class="fba-tooltip-title">過去の販売数量</div><div class="fba-tooltip-row"><span>・7日：</span><span>${item.salesHistory.days7}個</span></div><div class="fba-tooltip-row"><span>・30日：</span><span>${item.salesHistory.days30}個</span></div><div class="fba-tooltip-row"><span>・60日：</span><span>${item.salesHistory.days60}個</span></div><div class="fba-tooltip-row"><span>・90日：</span><span>${item.salesHistory.days90}個</span></div></div>`
    },
    isNearDate (dateStr) {
      const today = new Date()
      const limit = new Date()
      limit.setDate(today.getDate() + 14)

      const date = new Date(dateStr)
      return date <= limit
    },
    getAdditionalQtyClass (qty) {
      if (qty > 0) return 'bg-positive'
      return 'bg-negative'
    }
  },
  mounted () {
    let logParams = {
      name: eventCode.salemon_agent.fba_product_sales.show,
      properties: {}
    }
    EventService.postTrackLog(logParams)
  }
}
</script>

<style scoped>
.panel-body {
  display: flex;
  flex-direction: column;
  gap: 15px;
  position: relative;
}
.panel-body::before {
  display: unset;
  content: unset;
}

/* Filter container */
.filter-container {
  background-color: #fff;
  border: 1px solid #ccc;
  padding: 20px;
  border-radius: 8px;
  width: 100%;
  overflow: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
}
.filter-tabs {
  display: flex;
  gap: 3px;
  margin-top: 2px;
}
.filter-keyword {
  flex: 1;
  position: relative;
  min-width: 280px;
}
.search-icon-left {
  position: absolute;
  left: 10px;
  top: 50%;
  transform: translateY(-50%);
  font-size: 20px;
  z-index: 1;
}
.search-input {
  width: 100%;
  padding: 6px;
  padding-left: 40px;
  border: 1px solid #ccc;
  border-radius: 8px;
  font-size: 15px;
  color: #000;
}
.search-input:focus {
  outline: none;
  border-color: #0066ff;
  box-shadow: 0 0 0 4px rgba(0, 102, 255, 0.1);
}
.clear-search {
  position: absolute;
  right: 10px;
  top: 50%;
  transform: translateY(-50%);
  background: none;
  border: none;
  color: #ccc;
  cursor: pointer;
  font-size: 18px;
  padding: 4px;
  opacity: 0;
  transition: all 0.3s ease;
}
.search-input:not(:placeholder-shown) ~ .clear-search {
  opacity: 1;
}
.clear-search:hover {
  color: #000;
}
.filter-actions {
  margin-left: auto;
  display: flex;
  align-items: center;
  flex-wrap: nowrap;
}
.filter-tab {
  border: none;
  border-radius: 4px;
  padding: 4px 14px;
  font-size: 14px;
  cursor: pointer;
}
.filter-tab--all {
  background-color: #D9D9D9;
  color: #000;
}
.filter-tab--all.filter-tab--active {
  background-color: #333F50;
  color: #fff;
}
.filter-tab--recommended {
  background-color: #D9D9D9;
  color: #000;
}
.filter-tab--recommended.filter-tab--active {
  background-color: #0070C0;
  color: #fff;
}

/* Toolbar */
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  flex-wrap: wrap;
}
.toolbar-left {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
}
.snapshot-date {
  background-color: #fff;
  border: 1px solid #ccc;
  color: #0070C0;
  padding: 5px 15px;
  font-size: 20px;
  white-space: nowrap;
}
.snapshot-note {
  font-size: 14px;
  color: #333;
}
.toolbar-right {
  display: flex;
  align-items: center;
  gap: 10px;
  flex-wrap: nowrap;
}

/* Table */
.table-wrapper {
  overflow-x: auto;
}
.fba-table {
  font-size: 14px;
  white-space: nowrap;
  border-collapse: collapse;
  width: 100%;
}
.fba-table th,
.fba-table td {
  border: 1px solid #d9d9d9;
  padding: 6px 10px;
  vertical-align: middle;
}
.fba-table thead th {
  text-align: center;
  font-weight: bold;
  vertical-align: middle;
}
.fba-table {
  table-layout: fixed;
}
.col-product {
  background-color: #f9f9f9;
  color: #595959;
  font-weight: bold;
  vertical-align: middle;
}
.fba-table tbody td.col-product {
  background-color: #fff;
  font-weight: normal;
}
.col-stockcrew {
  width: 140px;
  background-color: #00B8BE;
  color: #fff;
}
.col-amazon {
  width: 160px;
  background-color: #232F3E;
  color: #fff;
}
.th-group {
  font-size: 11px;
  font-weight: normal;
  opacity: 0.85;
  margin-bottom: 2px;
}
.th-sub {
  font-size: 13px;
  font-weight: bold;
  white-space: nowrap;
}
.sortable-th {
  cursor: pointer;
  user-select: none;
}
.sortable-th:hover {
  opacity: 0.85;
}
.sortable-th .fa {
  margin-left: 4px;
  font-size: 12px;
}
.fba-table tbody tr:hover {
  background-color: #f5f5f5;
}
.fba-table tbody td {
  background-color: #fff;
}
.fba-table tbody td.col-stockcrew .qty {
  color: #00B8BE;
}
.fba-table tbody td.col-amazon-data .qty {
  color: #232F3E;
}

/* Product cell */
.no-wrap {
  flex-wrap: nowrap;
}
.product-img {
  width: 60px;
  height: 60px;
  object-fit: contain;
  flex-shrink: 0;
}
.product-img-placeholder {
  width: 60px;
  height: 60px;
  background-color: #f0f0f0;
  flex-shrink: 0;
}
.product-info {
  display: flex;
  flex-direction: column;
  gap: 4px;
  min-width: 0;
}
.product-name {
  display: block;
  white-space: normal;
  word-break: break-word;
  font-size: 14px;
  color: #0070C0;
}
.product-meta {
  flex-wrap: wrap;
  gap: 16px;
  font-size: 12px;
}
.meta-pair {
  display: inline-flex;
  align-items: center;
  gap: 1px;
}
.meta-label {
  font-size: 12px;
  color: #595959;
  font-weight: 400;
}
.meta-tag {
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 3px;
  padding: 1px 6px;
  font-size: 12px;
  color: #595959;
  font-weight: 400;
  width: 120px;
  text-align: center;
}

/* Data cells */
.col-amazon-data {
  background-color: #fff;
}
.cell-with-icon {
  position: relative;
  text-align: right;
  vertical-align: middle;
  padding-right: 16px !important;
}
.cell-icon {
  position: absolute;
  top: 4px;
  left: 6px;
}
.sc-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
.amazon-image {
  width: 32px;
  height: 32px;
  object-fit: contain;
}
.qty {
  font-size: 16px;
  font-weight: bold;
}
.restock-date {
  color: #c00000;
  font-weight: bold;
}
.col-amazon-data.bg-positive {
  background-color: #F4FDF7;
  color: #008236;
}
.col-amazon-data.bg-negative {
  background-color: #F2F2F2;
  color: #595959;
}
.no-data {
  padding: 30px;
  color: #999;
  font-size: 14px;
}

@media (max-width: 768px) {
  .filter-keyword {
    width: 100%;
    min-width: unset;
  }
  .search-input {
    font-size: 14px;
  }
  .table-toolbar {
    flex-direction: column;
    align-items: flex-start;
  }
}
</style>

<style>
.fba-tooltip {
  font-size: 13px;
  line-height: 1.8;
  white-space: nowrap;
  text-align: left;
}
.fba-tooltip-title {
  margin-bottom: 2px;
}
.fba-tooltip-row {
  display: flex;
  gap: 4px;
  padding-left: 8px;
}
</style>



// WEBPACK FOOTER //
// src/view/salemonAgent/FbaProductSales.vue